import * as React from "react";
import { useState, useRef } from "react";
import { Lesson, LessonFile, LessonFAQ } from "@/types/course";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { X, Plus, Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

interface LessonEditorProps {
  moduleId: string;
  lesson: Lesson | null;
  onLessonChange: (lesson: Lesson) => void;
  onLessonDelete: () => void;
  isNew?: boolean;
}

export function LessonEditor({ 
  moduleId, 
  lesson, 
  onLessonChange, 
  onLessonDelete,
  isNew = false
}: LessonEditorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [files, setFiles] = useState<LessonFile[]>(lesson?.files || []);
  const [faqs, setFaqs] = useState<LessonFAQ[]>(lesson?.faqs || []);
  const [tab, setTab] = useState("content");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    description?: string;
    videoUrl?: string;
  }>({});
  
  const videoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: lesson?.title || "",
    description: lesson?.description || "",
    videoUrl: lesson?.video_url || "",
    isPreview: lesson?.is_preview || false,
  });

  const [newFaq, setNewFaq] = useState({
    question: "",
    answer: ""
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === "title" || name === "description") {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isPreview: checked,
    }));
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      toast.error("Video file is too large. Maximum size is 100MB.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${uuidv4()}.${fileExt}`;
      
      // Start a progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);
      
      const { error: uploadError, data } = await supabase.storage
        .from('lesson-videos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      clearInterval(progressInterval);
      
      if (uploadError) throw uploadError;

      setUploadProgress(100);

      const { data: { publicUrl } } = supabase.storage
        .from('lesson-videos')
        .getPublicUrl(filePath);

      setFormData((prev) => ({
        ...prev,
        videoUrl: publicUrl,
      }));
      
      setValidationErrors(prev => ({ ...prev, videoUrl: undefined }));

      toast.success("Video uploaded successfully");
    } catch (error) {
      console.error("Error uploading video:", error);
      toast.error("Failed to upload video");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      toast.error("File is too large. Maximum size is 50MB.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${uuidv4()}.${fileExt}`;
      
      // Start a progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);
      
      const { error: uploadError } = await supabase.storage
        .from('lesson-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      clearInterval(progressInterval);
      
      if (uploadError) throw uploadError;

      setUploadProgress(100);

      const { data: { publicUrl } } = supabase.storage
        .from('lesson-files')
        .getPublicUrl(filePath);

      if (lesson?.id) {
        const { data, error } = await supabase
          .from('lesson_files')
          .insert({
            lesson_id: lesson.id,
            file_url: publicUrl,
            file_name: file.name
          })
          .select()
          .single();

        if (error) throw error;

        setFiles((prev) => [...prev, data as LessonFile]);
      } else {
        const tempFile: LessonFile = {
          id: uuidv4(),
          lesson_id: "",
          file_url: publicUrl,
          file_name: file.name,
          created_at: new Date().toISOString()
        };
        setFiles((prev) => [...prev, tempFile]);
      }

      toast.success("File uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      if (!isNew && lesson?.id) {
        const file = files.find(f => f.id === fileId);
        if (!file) return;

        const filePathMatch = file.file_url.match(/[^/]+$/);
        if (filePathMatch) {
          const filePath = filePathMatch[0];
          await supabase.storage.from('lesson-files').remove([filePath]);
        }

        const { error } = await supabase
          .from('lesson_files')
          .delete()
          .eq('id', fileId);

        if (error) throw error;
      }

      setFiles(files.filter(file => file.id !== fileId));
      toast.success("File removed successfully");
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to remove file");
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error("Both question and answer are required");
      return;
    }

    try {
      if (!isNew && lesson?.id) {
        const { data, error } = await supabase
          .from('lesson_faqs')
          .insert({
            lesson_id: lesson.id,
            question: newFaq.question,
            answer: newFaq.answer
          })
          .select()
          .single();

        if (error) throw error;
        
        setFaqs((prev) => [...prev, data as LessonFAQ]);
      } else {
        const tempFaq: LessonFAQ = {
          id: uuidv4(),
          lesson_id: "",
          question: newFaq.question,
          answer: newFaq.answer,
          created_at: new Date().toISOString()
        };
        setFaqs((prev) => [...prev, tempFaq]);
      }

      setNewFaq({ question: "", answer: "" });
      toast.success("FAQ added successfully");
    } catch (error) {
      console.error("Error adding FAQ:", error);
      toast.error("Failed to add FAQ");
    }
  };

  const handleDeleteFaq = async (faqId: string) => {
    try {
      if (!isNew && lesson?.id) {
        const { error } = await supabase
          .from('lesson_faqs')
          .delete()
          .eq('id', faqId);

        if (error) throw error;
      }

      setFaqs(faqs.filter(faq => faq.id !== faqId));
      toast.success("FAQ removed successfully");
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to remove FAQ");
    }
  };

  const validateForm = () => {
    const errors: {
      title?: string;
      description?: string;
      videoUrl?: string;
    } = {};
    
    if (!formData.title.trim()) {
      errors.title = "Lesson title is required";
    }
    
    if (!formData.description?.trim()) {
      errors.description = "Lesson description is required";
    }
    
    if (!formData.videoUrl) {
      errors.videoUrl = "Lesson video is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveLesson = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      let lessonId = lesson?.id;
      let newLesson: Lesson;

      if (isNew) {
        const { data, error } = await supabase
          .from('lessons')
          .insert({
            module_id: moduleId,
            title: formData.title,
            description: formData.description,
            video_url: formData.videoUrl,
            is_preview: formData.isPreview
          })
          .select()
          .single();

        if (error) throw error;
        
        lessonId = data.id;
        newLesson = data as Lesson;
      } else {
        const { data, error } = await supabase
          .from('lessons')
          .update({
            title: formData.title,
            description: formData.description,
            video_url: formData.videoUrl,
            is_preview: formData.isPreview
          })
          .eq('id', lesson!.id)
          .select()
          .single();

        if (error) throw error;
        
        newLesson = data as Lesson;
      }

      if (isNew && lessonId) {
        for (const file of files) {
          await supabase
            .from('lesson_files')
            .insert({
              lesson_id: lessonId,
              file_url: file.file_url,
              file_name: file.file_name
            });
        }

        for (const faq of faqs) {
          await supabase
            .from('lesson_faqs')
            .insert({
              lesson_id: lessonId,
              question: faq.question,
              answer: faq.answer
            });
        }
      }

      const { data: fileData } = await supabase
        .from('lesson_files')
        .select()
        .eq('lesson_id', lessonId);

      const { data: faqData } = await supabase
        .from('lesson_faqs')
        .select()
        .eq('lesson_id', lessonId);

      newLesson.files = fileData as LessonFile[];
      newLesson.faqs = faqData as LessonFAQ[];

      onLessonChange(newLesson);
      toast.success(isNew ? "Lesson created successfully" : "Lesson updated successfully");
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error("Failed to save lesson");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLesson = async () => {
    if (!lesson || isNew) return;

    try {
      const { error } = await supabase
        .from('lessons')
        .delete()
        .eq('id', lesson.id);

      if (error) throw error;

      onLessonDelete();
      toast.success("Lesson deleted successfully");
    } catch (error) {
      console.error("Error deleting lesson:", error);
      toast.error("Failed to delete lesson");
    }
  };

  if (!lesson && !isNew) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <div className="text-center">
          <h3 className="text-lg font-medium mb-2">No Lesson Selected</h3>
          <p className="text-muted-foreground">
            Select a lesson from the sidebar or create a new one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          {isNew ? "Create New Lesson" : "Edit Lesson"}
        </h2>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDeleteLesson}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          <Button 
            size="sm"
            onClick={handleSaveLesson}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="border rounded-md">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
          </TabsList>
          
          <TabsContent value="content" className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center">
                Lesson Title <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter lesson title"
                className={`w-full ${validationErrors.title ? 'border-destructive' : ''}`}
              />
              {validationErrors.title && (
                <p className="text-sm text-destructive">{validationErrors.title}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center">
                Description <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description || ""}
                onChange={handleInputChange}
                placeholder="Enter lesson description"
                className={`w-full min-h-[120px] ${validationErrors.description ? 'border-destructive' : ''}`}
              />
              {validationErrors.description && (
                <p className="text-sm text-destructive">{validationErrors.description}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center">
                Video <span className="text-destructive ml-1">*</span>
              </Label>
              {formData.videoUrl ? (
                <div className="border rounded-md p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Video Uploaded</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-destructive"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, videoUrl: "" }));
                        setValidationErrors(prev => ({...prev, videoUrl: "Lesson video is required"}));
                      }}
                    >
                      <X className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </div>
                  <video 
                    src={formData.videoUrl} 
                    controls 
                    className="w-full rounded" 
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              ) : (
                <div className={`flex items-center justify-center border ${validationErrors.videoUrl ? 'border-destructive' : 'border-dashed'} rounded-md p-8`}>
                  <div className="text-center">
                    <Upload className={`h-8 w-8 mx-auto ${validationErrors.videoUrl ? 'text-destructive' : 'text-muted-foreground'} mb-2`} />
                    <p className="text-sm mb-2">Upload a video file</p>
                    <p className="text-xs text-muted-foreground mb-4">MP4, WEBM or MOV up to 100MB</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {isUploading ? `Uploading ${uploadProgress}%` : "Select File"}
                    </Button>
                    <input
                      type="file"
                      ref={videoInputRef}
                      onChange={handleVideoUpload}
                      className="hidden"
                      accept="video/mp4,video/webm,video/quicktime"
                    />
                    {validationErrors.videoUrl && (
                      <p className="text-sm text-destructive mt-2">{validationErrors.videoUrl}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2 pt-4">
              <Switch
                id="isPreview"
                checked={formData.isPreview}
                onCheckedChange={handleSwitchChange}
              />
              <Label htmlFor="isPreview">Make this lesson available as a preview</Label>
            </div>
          </TabsContent>
          
          <TabsContent value="files" className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-md font-medium">Downloadable Files</h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {isUploading ? `Uploading ${uploadProgress}%` : "Add File"}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
              
              {files.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No files added yet. Add files for students to download.
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <Card key={file.id}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-primary" />
                            <div>
                              <p className="font-medium text-sm">{file.file_name}</p>
                              <a 
                                href={file.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="faqs" className="p-4">
            <div className="space-y-4">
              <h3 className="text-md font-medium">Frequently Asked Questions</h3>
              
              <div className="space-y-3 border rounded-md p-3">
                <Label htmlFor="question">Question</Label>
                <Input
                  id="question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                  placeholder="Enter a question"
                />
                
                <Label htmlFor="answer">Answer</Label>
                <Textarea
                  id="answer"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  placeholder="Enter the answer"
                  className="min-h-[100px]"
                />
                
                <Button 
                  className="w-full"
                  onClick={handleAddFaq}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add FAQ
                </Button>
              </div>
              
              {faqs.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  No FAQs added yet. Add frequently asked questions to help your students.
                </div>
              ) : (
                <Accordion type="multiple" className="w-full">
                  {faqs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <div className="flex justify-between items-center">
                        <AccordionTrigger>{faq.question}</AccordionTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive mr-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFaq(faq.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <AccordionContent>
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
